/**
 * UnifiedTagModal Container Component
 *
 * A single, unified tag management interface that combines tag selection
 * and management into one intuitive modal. Provides a seamless experience
 * for both adding tags to players and managing the overall tag library.
 *
 * Uses Apollo Client with board-specific tag queries to display accurate
 * tag colors for the current kanban board context.
 *
 * Features:
 * - Single modal for both selecting and managing tags
 * - Inline tag creation with automatic color assignment
 * - Edit and delete capabilities for existing tags
 * - Smart search with create-from-search functionality
 * - Board-specific tag colors from listKanbanBoardTags query
 * - Apollo-based reactive updates for better UX
 * @module features/player-kanban/components/UnifiedTagModal
 */

import * as Crypto from "expo-crypto";
import { Trash2 } from "lucide-react-native";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import ConfirmDialog from "@/components/molecules/ConfirmDialog";

import UnifiedTagModalView from "./UnifiedTagModalView";
import { useBoardTagColors } from "@/features/player-kanban/hooks/useBoardTagColors";
import { useTagCrud } from "@/features/player-kanban/hooks/useTagCrud";

import { useListKanbanTagsQuery } from "@/generated/graphql";
import { normalizeForSearch, searchIncludes } from "@/utils/normalizeForSearch";

// Default colors for tags - matching the service predefinedColors
const DEFAULT_TAG_COLORS = [
  "#EF4444", // red
  "#F97316", // orange
  "#F59E0B", // amber
  "#EAB308", // yellow
  "#84CC16", // lime
  "#22C55E", // green
  "#10B981", // emerald
  "#06B6D4", // cyan
  "#0EA5E9", // sky
  "#3B82F6", // blue (primary)
  "#2563EB", // blue-600 (darker blue)
  "#1D4ED8", // blue-700 (deep blue)
  "#60A5FA", // blue-400 (light blue)
  "#D946EF", // fuchsia
  "#EC4899", // pink
  "#F43F5E", // rose
];

/**
 * Props for UnifiedTagModal container
 */
interface UnifiedTagModalContainerProps {
  isOpen: boolean;
  playerId?: string;
  playerTagIds?: string[];
  mode?: "select" | "manage";
  boardId?: string;
  onClose: () => void;
  onTagsUpdated?: (tagIds: string[]) => void;
}

/**
 * UnifiedTagModal Container - Manages the unified tag interface for selection and management
 * @param props - Container props
 * @param props.isOpen - Whether the modal is currently visible
 * @param props.playerId - ID of the player whose tags are being managed (for select mode)
 * @param props.playerTagIds - Array of tag IDs currently assigned to the player
 * @param props.mode - Initial modal mode: 'select' for tag selection or 'manage' for tag management
 * @param props.onClose - Callback fired when the modal is closed
 * @param props.onTagsUpdated - Callback fired with updated tag IDs when tags are applied in select mode
 * @param props.boardId - Optional board ID for context-specific tag operations and analytics
 */
const UnifiedTagModalContainer = ({
  isOpen,
  playerId,
  playerTagIds = [],
  mode = "select",
  boardId = "",
  onClose,
  onTagsUpdated,
}: UnifiedTagModalContainerProps) => {
  // Apollo queries and mutations
  // Fetch ALL tags (always needed to show complete tag list)
  const { data: tagsData, loading: isLoadingTags } = useListKanbanTagsQuery({
    variables: { first: 100 },
    fetchPolicy: "cache-and-network",
  });

  // Board tag colors with fallback support
  const {
    boardTagColors,
    isLoading: isLoadingBoardTags,
    getTagColor,
    saveBoardTagColor,
    storeFallbackColor,
  } = useBoardTagColors(boardId);

  const {
    createTag,
    updateTag,
    deleteTag,
    isCreating: isCreatingFromHook,
  } = useTagCrud(boardId);

  // Local state
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>(playerTagIds);
  const [editingTagId, setEditingTagId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState("");
  const [isCreatingLocal, setIsCreatingLocal] = useState(false);
  const [currentMode, setCurrentMode] = useState(mode);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [selectedColor, setSelectedColor] = useState(DEFAULT_TAG_COLORS[0]);
  const [editingColorTagId, setEditingColorTagId] = useState<string | null>(
    null
  );

  // Track previous isOpen state to detect modal open transition
  const prevIsOpenRef = useRef(false);

  // Convert Apollo data to frontend tag format with board-specific colors
  const tags = useMemo(() => {
    if (!tagsData?.listKanbanTags?.edges) {
      return [];
    }

    return tagsData.listKanbanTags.edges.map(edge => {
      const tag = edge.node;
      return {
        id: tag.id,
        name: tag.name,
        cardCount: tag.cardCount,
        color: getTagColor(tag.id),
        createdAt: tag.createdAt,
        updatedAt: tag.updatedAt,
      };
    });
  }, [tagsData, getTagColor]);

  // Combine loading states - include both query loading states
  const isLoading = isLoadingTags || isLoadingBoardTags;
  const isCreating = isCreatingFromHook || isCreatingLocal || isLoading;

  // Filter tags based on search (accent-insensitive)
  const filteredTags = useMemo(() => {
    if (!searchQuery.trim()) return tags;

    return tags.filter(tag => searchIncludes(tag.name, searchQuery));
  }, [tags, searchQuery]);

  // Check if search query matches any existing tag (accent-insensitive)
  const canCreateFromSearch = useMemo(() => {
    if (!searchQuery.trim()) return false;
    const normalizedQuery = normalizeForSearch(searchQuery);
    return !tags.some(tag => normalizeForSearch(tag.name) === normalizedQuery);
  }, [tags, searchQuery]);

  // Get next available color for new tags
  const getNextColor = useCallback(() => {
    const usedColors = new Set(tags.map(t => t.color));
    const availableColors = DEFAULT_TAG_COLORS.filter(
      color => !usedColors.has(color)
    );
    // Use first available color, or select randomly from all colors using secure random
    const colorsToPickFrom =
      availableColors.length > 0 ? availableColors : DEFAULT_TAG_COLORS;
    const randomBytes = Crypto.getRandomBytes(4);
    const randomValue =
      (randomBytes[0] << 24) |
      (randomBytes[1] << 16) |
      (randomBytes[2] << 8) |
      randomBytes[3];
    const randomIndex = Math.abs(randomValue) % colorsToPickFrom.length;
    return colorsToPickFrom[randomIndex];
  }, [tags]);

  // Handle tag selection toggle
  const handleTagToggle = useCallback((tagId: string) => {
    setSelectedTagIds(prev => {
      if (prev.includes(tagId)) {
        return prev.filter(id => id !== tagId);
      }
      return [...prev, tagId];
    });
  }, []);

  // Handle creating tag from search
  const handleCreateFromSearch = useCallback(async () => {
    if (!searchQuery.trim() || !canCreateFromSearch) return;

    setIsCreatingLocal(true);
    try {
      const colorToUse = selectedColor || getNextColor();
      const newTag = await createTag(searchQuery.trim(), colorToUse);

      if (newTag) {
        // Store fallback and board-specific color
        storeFallbackColor(newTag.id, colorToUse);
        await saveBoardTagColor(newTag.id, newTag.name, colorToUse);

        if (currentMode === "select") {
          setSelectedTagIds(prev => [...prev, newTag.id]);
        }
      }

      setSearchQuery("");
      setShowColorPicker(false);
      // Reset to next available color for next creation
      setSelectedColor(getNextColor());
    } catch (error) {
      console.error("Failed to create tag:", error);
    } finally {
      setIsCreatingLocal(false);
    }
  }, [
    searchQuery,
    canCreateFromSearch,
    createTag,
    selectedColor,
    getNextColor,
    currentMode,
    storeFallbackColor,
    saveBoardTagColor,
  ]);

  // Handle inline tag creation
  const handleQuickCreate = useCallback(
    async (name: string) => {
      if (!name.trim()) return;

      setIsCreatingLocal(true);
      try {
        const colorToUse = getNextColor();
        const newTag = await createTag(name.trim(), colorToUse);

        if (newTag) {
          // Store fallback and board-specific color
          storeFallbackColor(newTag.id, colorToUse);
          await saveBoardTagColor(newTag.id, newTag.name, colorToUse);

          if (currentMode === "select") {
            setSelectedTagIds(prev => [...prev, newTag.id]);
          }
        }
      } catch (error) {
        console.error("Failed to create tag:", error);
      } finally {
        setIsCreatingLocal(false);
      }
    },
    [
      createTag,
      getNextColor,
      currentMode,
      storeFallbackColor,
      saveBoardTagColor,
    ]
  );

  // Handle tag editing
  const handleStartEdit = useCallback((tagId: string, currentName: string) => {
    setEditingTagId(tagId);
    setEditingName(currentName);
  }, []);

  const handleSaveEdit = useCallback(async () => {
    if (!editingTagId || !editingName.trim()) return;

    try {
      await updateTag(editingTagId, { name: editingName.trim() });
      setEditingTagId(null);
      setEditingName("");
    } catch (error) {
      console.error("Failed to update tag:", error);
    }
  }, [editingTagId, editingName, updateTag]);

  const handleCancelEdit = useCallback(() => {
    setEditingTagId(null);
    setEditingName("");
  }, []);

  /*
   * Deleting a tag detaches it from every player that carries it, so it asks
   * first (SE-6554). The trash glyph now only ARMS the dialog; the mutation
   * runs from its confirm. Previously a single tap deleted outright, with the
   * usage count on the row as the only hint of the blast radius.
   */
  const [pendingDeleteTagId, setPendingDeleteTagId] = useState<string | null>(
    null
  );
  const [isDeleting, setIsDeleting] = useState(false);

  const pendingDeleteTag = useMemo(
    () => tags.find(tag => tag.id === pendingDeleteTagId) ?? null,
    [tags, pendingDeleteTagId]
  );

  const handleDeleteTag = useCallback((tagId: string) => {
    setPendingDeleteTagId(tagId);
  }, []);

  const handleCancelDelete = useCallback(() => {
    setPendingDeleteTagId(null);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!pendingDeleteTagId) return;

    setIsDeleting(true);
    try {
      await deleteTag(pendingDeleteTagId);
      setSelectedTagIds(prev => prev.filter(id => id !== pendingDeleteTagId));
      setPendingDeleteTagId(null);
    } catch (error) {
      console.error("Failed to delete tag:", error);
    } finally {
      setIsDeleting(false);
    }
  }, [deleteTag, pendingDeleteTagId]);

  // Handle applying selected tags - also saves board-specific colors for newly added tags
  const handleApply = useCallback(async () => {
    if (!boardId) {
      onTagsUpdated?.(selectedTagIds);
      onClose();
      return;
    }

    // Find tags being newly added (in selectedTagIds but not in playerTagIds)
    const newlyAddedTagIds = selectedTagIds.filter(
      id => !playerTagIds.includes(id)
    );

    // Save board-specific colors for newly added tags that don't have one yet
    const colorSavePromises = newlyAddedTagIds
      .filter(tagId => !boardTagColors[tagId]) // Only tags without board color
      .map(tagId => {
        const tag = tags.find(t => t.id === tagId);
        if (!tag) return null;
        return saveBoardTagColor(tagId, tag.name, tag.color);
      })
      .filter(Boolean);

    // Save colors in parallel (don't block the apply)
    Promise.all(colorSavePromises).catch(error => {
      console.error("Failed to save some tag colors:", error);
    });

    onTagsUpdated?.(selectedTagIds);
    onClose();
  }, [
    selectedTagIds,
    playerTagIds,
    boardId,
    boardTagColors,
    tags,
    saveBoardTagColor,
    onTagsUpdated,
    onClose,
  ]);

  // Handle mode switching
  const handleModeSwitch = useCallback(() => {
    setCurrentMode(prev => (prev === "select" ? "manage" : "select"));
  }, []);

  // Handle color selection
  const handleColorSelect = useCallback((color: string) => {
    setSelectedColor(color);
  }, []);

  // Handle toggling color picker
  const handleToggleColorPicker = useCallback(() => {
    setShowColorPicker(prev => !prev);
  }, []);

  // Handle starting color edit for a tag
  const handleStartColorEdit = useCallback(
    (tagId: string, currentColor: string) => {
      setEditingColorTagId(tagId);
      setSelectedColor(currentColor);
    },
    []
  );

  // Handle saving color change for a tag
  const handleSaveColorChange = useCallback(
    async (color: string) => {
      if (!editingColorTagId) return;

      // Find the tag being edited to get its name
      const editingTag = tags.find(t => t.id === editingColorTagId);

      try {
        await saveBoardTagColor(
          editingColorTagId,
          editingTag?.name || "",
          color
        );
        setEditingColorTagId(null);
      } catch (error) {
        console.error("Failed to update tag color:", error);
      }
    },
    [editingColorTagId, tags, saveBoardTagColor]
  );

  // Handle canceling color edit
  const handleCancelColorEdit = useCallback(() => {
    setEditingColorTagId(null);
  }, []);

  // Calculate tag usage counts
  const tagUsageCounts = useMemo(() => {
    return tags.reduce<Record<string, number>>(
      (counts, tag) => ({
        ...counts,
        [tag.id]: tag.cardCount || 0,
      }),
      {}
    );
  }, [tags]);

  // Initialize state only when modal transitions from closed to open
  // This prevents infinite loops when playerTagIds changes during the modal session
  useEffect(() => {
    const wasOpen = prevIsOpenRef.current;
    prevIsOpenRef.current = isOpen;

    // Only initialize when modal OPENS (transition from closed to open)
    if (isOpen && !wasOpen) {
      setCurrentMode(mode);
      setSearchQuery("");
      setEditingTagId(null);
      setSelectedTagIds(playerTagIds);
    }
  }, [isOpen, mode, playerTagIds]);

  return (
    <>
      <UnifiedTagModalView
        isOpen={isOpen}
        mode={currentMode}
        searchQuery={searchQuery}
        filteredTags={filteredTags}
        selectedTagIds={selectedTagIds}
        editingTagId={editingTagId}
        editingName={editingName}
        isCreating={isCreating}
        canCreateFromSearch={canCreateFromSearch}
        tagUsageCounts={tagUsageCounts}
        showApplyButton={!!playerId}
        showColorPicker={showColorPicker}
        selectedColor={selectedColor}
        availableColors={DEFAULT_TAG_COLORS}
        editingColorTagId={editingColorTagId}
        onClose={onClose}
        onSearchChange={setSearchQuery}
        onTagToggle={handleTagToggle}
        onCreateFromSearch={handleCreateFromSearch}
        onQuickCreate={handleQuickCreate}
        onStartEdit={handleStartEdit}
        onSaveEdit={handleSaveEdit}
        onCancelEdit={handleCancelEdit}
        onEditNameChange={setEditingName}
        onDeleteTag={handleDeleteTag}
        onColorSelect={handleColorSelect}
        onToggleColorPicker={handleToggleColorPicker}
        onStartColorEdit={handleStartColorEdit}
        onSaveColorChange={handleSaveColorChange}
        onCancelColorEdit={handleCancelColorEdit}
        onApply={handleApply}
        onModeSwitch={handleModeSwitch}
      />
      <ConfirmDialog
        testID="delete-tag-dialog"
        // Opens on the INTENT to delete, not on the name lookup succeeding:
        // the dialog must appear even if the row's tag has dropped out of the
        // list.
        isOpen={Boolean(pendingDeleteTagId)}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        icon={Trash2}
        tone="danger"
        title={`Delete "${pendingDeleteTag?.name ?? ""}"?`}
        body={[
          "This removes the tag from every player that carries it.",
          `${tagUsageCounts[pendingDeleteTag?.id ?? ""] ?? 0} players currently have it.`,
        ]}
        cancelLabel="Cancel"
        confirmLabel={isDeleting ? "Deleting..." : "Delete tag"}
        isConfirming={isDeleting}
      />
    </>
  );
};

export default UnifiedTagModalContainer;
